import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import Login from './pages/Login';
import Register from './pages/Register';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import PricingManagement from './pages/PricingManagement';
import Masters from './pages/Masters';
import Reports from './pages/Reports';
import History from './pages/History';
import LunchUpdates from './pages/LunchUpdates';
import Permissions from './pages/Permissions';
import SuperuserDashboard from './pages/SuperuserDashboard';
import ParentDashboard from './pages/ParentDashboard';
import ParentLunchUpdates from './pages/ParentLunchUpdates';
import Plans from './pages/Plans';
import ParentPlans from './pages/ParentPlans';
import Verification from './pages/Verification';
import Leaves from './pages/Leaves';
import Notifications from './pages/Notifications';
import BranchInchargeDashboard from './pages/BranchInchargeDashboard';
import Holidays from './pages/Holidays';
import ParentHolidays from './pages/ParentHolidays';
import DeliveryShiftStartPage from './pages/delivery/DeliveryShiftStart';
import DeliveryDashboardPage from './pages/delivery/DeliveryDashboard';
import DeliveryPickupsPage from './pages/delivery/DeliveryPickups';
import DeliveryHolidaysPage from './pages/delivery/DeliveryHolidays';
import SubscriptionDataPage from './pages/SubscriptionData';
import TeleRecordPage from './pages/TeleRecord';
import TelecallerDashboardPage from './pages/TelecallerDashboard';
import LeadsPage from './pages/Leads';
import TrackDeliveryPage from './pages/TrackDelivery';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { storage } from './lib/storage';
import { isTokenExpired } from './lib/jwt';
import { getDeliveryBoyDetailsService } from './services/deliveryAgent';
import { getUserIdFromToken } from './lib/jwt';
import { getUserDetailsService } from './services/auth';
import DeliveryAssignmentPage from './pages/DeliveryAssignment';
import { useDispatch } from 'react-redux';
import { setRefreshTokenCookie } from './lib/clientCookie';
import { updateUserData } from './lib/store/slices/UserSlice';
import { clearPermissions } from './lib/store/slices/PermissionsSlice';
import ReferAFriendPage from './pages/ReferAFriend';
import OffersPage from './pages/Offers';
import ReferralPage from './pages/Referral';
import ResetPasswordPage from './pages/ResetPassword';
import NoticesPage from './pages/Notices';
import SpecialOffersPage from './pages/SpecialOffers';
import UpcomingBirthdays from './pages/UpcomingBirthdays';
import TrailOfferPage from './pages/TrailOffer';
import MobileUpdatesPage from './pages/MobileUpdates';
import PaymentSummaryPage from './pages/PaymentSummary';
import PaymentListPage from './pages/PaymentList';
import PaymentPendingUsers from './components/users/PaymentPendingUsers';

function getDashboardPath(userData?: any): string {
  if (userData?.isSuperuser) return '/admin-dashboard';
  const r = (userData?.role || userData?.user?.role)?.toLowerCase().replace(/[\s_]+/g, '');
  if (r === 'parent') return '/dashboard';
  if (r === 'zonemanager') return '/zone-manager-dashboard';
  if (r === 'branchincharge') return '/branch-incharge-dashboard';
  if (r === 'deliveryboy') return '/delivery/dashboard';
  if (r === 'telecaller') return '/telecaller-dashboard';
  return '/admin-dashboard';
}

function SuperuserRoute({ children }: { children: React.ReactNode }) {
  const userData = storage.getUserData();
  if (!userData?.isSuperuser) {
    return <Navigate to={getDashboardPath(userData)} replace />;
  }
  return <>{children}</>;
}

/** Redirects Branch Incharge and non-admin roles away from admin-only routes */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const userData = storage.getUserData();
  const r = (userData?.role || userData?.user?.role)?.toLowerCase().replace(/[\s_]+/g, '');
  const isAdmin = userData?.isSuperuser || ['admin', 'superuser'].includes(r ?? '');
  if (!isAdmin) {
    return <Navigate to={getDashboardPath(userData)} replace />;
  }
  return <>{children}</>;
}

/** Redirects to role-based dashboard if authenticated, otherwise to /login */
function SmartRedirect() {
  const navigate = useNavigate();
  const dispatch = useDispatch()

  useEffect(() => {
    globalThis.__LOGOUT__ = async () => {
      storage.setRefreshToken('');
      setRefreshTokenCookie('');
      storage.logout();
      dispatch(updateUserData(null));
      dispatch(clearPermissions());

      navigate("/login", { replace: true });
    };
  }, []);

  useEffect(() => {
    const run = async () => {
      const token = storage.getAuthToken();
      if (!token || isTokenExpired(token)) {
        navigate('/login', { replace: true });
        return;
      }

      let userData = storage.getUserData();

      // If no cached user data, fetch it
      if (!userData) {
        const userId = getUserIdFromToken(token);
        if (userId) {
          const res = await getUserDetailsService(Number(userId));
          if (res.status === 200 && res.data) {
            userData = res.data as any;
            storage.setUserData(userData);
          }
        }
      }

      const role = userData?.role || userData?.user?.role;
      const r = role?.toLowerCase().replace(/[\s_]+/g, '');

      if (r === 'deliveryboy') {
        const username = userData?.username || userData?.user?.username || '';
        if (username) {
          const deliveryRes = await getDeliveryBoyDetailsService(username);
          const deliveryData = deliveryRes?.data;
          const hasShift = deliveryData &&
            (Array.isArray(deliveryData)
              ? (deliveryData as any[]).length > 0
              : Object.keys(deliveryData as any).length > 0);
          navigate(hasShift ? '/delivery/dashboard' : '/delivery/shift-start', { replace: true });
          return;
        }
      }

      navigate(getDashboardPath(userData), { replace: true });
    };

    run();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500" />
    </div>
  );
}

/** Prevents already-authenticated users from accessing the login/register page */
function GuestRoute({ children }: { children: React.ReactNode }) {
  const token = storage.getAuthToken();
  if (token && !isTokenExpired(token)) {
    const userData = storage.getUserData();
    return <Navigate to={getDashboardPath(userData)} replace />;
  }
  return <>{children}</>;
}

function App() {
  useEffect(() => {
    const TAB_ID = sessionStorage.getItem("tabId") || Date.now().toString();
    sessionStorage.setItem("tabId", TAB_ID);

    const handleStorage = (event: StorageEvent) => {
      if (event.key === "auth-event") {
        const data = JSON.parse(event.newValue || "{}");

        if (data.tabId === TAB_ID) return; // 🔥 IMPORTANT FIX

        if (data.type === "LOGIN") {
          alert("You have been logged out due to login in another tab.");
          window.location.href = "/#/login";
        }

        if (data.type === "LOGOUT") {
          window.location.href = "/#/login";
        }
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            zIndex: 999999,
          },
        }}
      />
      <Routes>
        {/* Root — smart redirect based on auth state */}
        <Route path="/" element={<SmartRedirect />} />

        {/* Public Routes — redirect away if already logged in */}
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
        <Route path="/reset-password" element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />
        <Route
          path="/reset-password/:token"
          element={<ResetPasswordPage />}
        />
        {/* Protected Parent Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <ParentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/children"
          element={
            <ProtectedRoute>
              <Plans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent-plans"
          element={
            <ProtectedRoute>
              <ParentPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/verification"
          element={
            <ProtectedRoute>
              <Verification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leaves"
          element={
            <ProtectedRoute>
              <Leaves />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent-lunch-updates"
          element={
            <ProtectedRoute>
              <ParentLunchUpdates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />

        {/* Protected Admin Routes */}
        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-management"
          element={<ProtectedRoute><UserManagement /></ProtectedRoute>}
        />
        <Route
          path="/pricing-management"
          element={<ProtectedRoute><PricingManagement /></ProtectedRoute>}
        />
        <Route
          path="/masters"
          element={<ProtectedRoute><Masters /></ProtectedRoute>}
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <Reports />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={<ProtectedRoute><History /></ProtectedRoute>}
        />
        <Route
          path="/lunch-updates"
          element={<ProtectedRoute><LunchUpdates /></ProtectedRoute>}
        />
        <Route
          path="/holidays"
          element={<ProtectedRoute><Holidays /></ProtectedRoute>}
        />
        <Route
          path="/parent-holidays"
          element={<ProtectedRoute><ParentHolidays /></ProtectedRoute>}
        />
        <Route
          path="/permissions"
          element={
            <ProtectedRoute>
              <SuperuserRoute>
                <Permissions />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />

        {/* Other Dashboards */}
        <Route
          path="/superuser-dashboard"
          element={
            <ProtectedRoute>
              <SuperuserDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/branch-incharge-dashboard"
          element={
            <ProtectedRoute>
              <BranchInchargeDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pickups"
          element={
            <ProtectedRoute>
              <DeliveryPickupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription-data"
          element={
            <ProtectedRoute>
              <SubscriptionDataPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leads"
          element={
            <ProtectedRoute>
              <LeadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/telecaller-dashboard"
          element={
            <ProtectedRoute>
              <TelecallerDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tele-record"
          element={
            <ProtectedRoute>
              <TeleRecordPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/zone-manager-dashboard"
          element={
            <ProtectedRoute>
              <BranchInchargeDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/track-delivery"
          element={
            <ProtectedRoute>
              <TrackDeliveryPage />
            </ProtectedRoute>
          }
        />

        {/* Delivery Agent Routes */}
        <Route
          path="/delivery/shift-start"
          element={
            <ProtectedRoute>
              <DeliveryShiftStartPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/delivery/dashboard"
          element={
            <ProtectedRoute>
              <DeliveryDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/delivery/pickups"
          element={
            <ProtectedRoute>
              <DeliveryPickupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/delivery/holidays"
          element={
            <ProtectedRoute>
              <DeliveryHolidaysPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignment"
          element={
            <ProtectedRoute>
              <DeliveryAssignmentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/refer-a-friend"
          element={
            <ProtectedRoute>
              <ReferAFriendPage />
            </ProtectedRoute>
          }
        />
           <Route
          path="/referral"
          element={
            <ProtectedRoute>
              <ReferralPage />
            </ProtectedRoute>
          }
        />
           <Route
          path="/offers"
          element={
            <ProtectedRoute>
              <OffersPage />
            </ProtectedRoute>
          }
        />
         <Route
          path="/special-offers"
          element={
            <ProtectedRoute>
              <SpecialOffersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trail-offer"
          element={
            <ProtectedRoute>
              <TrailOfferPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notices"
          element={
            <ProtectedRoute>
              <NoticesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/upcoming-birthdays"
          element={
            <ProtectedRoute>
              <UpcomingBirthdays />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-pending-users"
          element={
            <ProtectedRoute>
              <PaymentPendingUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile-updates"
          element={
            <ProtectedRoute>
              <MobileUpdatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-summary"
          element={
            <ProtectedRoute>
              <PaymentSummaryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-list"
          element={
            <ProtectedRoute>
              <PaymentListPage />
            </ProtectedRoute>
          }
        />

        {/* 404 — smart redirect */}
        <Route path="*" element={<SmartRedirect />} />
      </Routes>
    </>
  );
}

export default App;
